import React from 'react'
import usePromo, { formatEur } from '../hooks/usePromo'

/**
 * <PromoBanner kind="individual|school" />
 *
 * Renders a calm highlighted promo box ONLY while the backend reports
 * `active: true`. Once the August 2026 window ends, the box disappears
 * automatically — no deploy needed.
 *
 * The numbers come from /api/promo/status, which is driven by the same
 * helper that calculates the real Stripe checkout amount, so the UI text
 * can never disagree with the card charge.
 *
 * Props:
 *   - kind:  'individual' (default) → 14,99 € → 11,24 €
 *            'school'                → 250,00 € → 187,50 €
 *   - className: optional extra wrapper classes
 *   - compact:   smaller variant (e.g. for hero pricing card)
 */
export default function PromoBanner({ kind = 'individual', className = '', compact = false }) {
  const promo = usePromo()
  if (promo.loading || !promo.active) return null

  const tier = promo[kind] || promo.individual
  const baseLabel = formatEur(tier.base_eur)
  const finalLabel = formatEur(tier.final_eur)
  const periodLabel = kind === 'school' ? '/ školský rok' : '/ školský rok'

  return (
    <div
      className={`relative rounded-2xl border border-amber-200 bg-amber-50 ${compact ? 'p-3 sm:p-4' : 'p-4 sm:p-5'} ${className}`}
      data-testid={`promo-banner-${kind}`}
      role="note"
      aria-label={promo.label_sk}
    >
      <div className="flex items-start gap-3">
        <span
          aria-hidden="true"
          className="flex-shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-lg bg-amber-500 text-white shadow-sm"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5a1.99 1.99 0 011.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.99 1.99 0 013 12V7a4 4 0 014-4z" />
          </svg>
        </span>

        <div className="min-w-0 flex-1">
          <p className="text-sm sm:text-base font-bold text-amber-900 leading-snug" data-testid={`promo-headline-${kind}`}>
            Zľava {promo.discount_pct} % pri registrácii do konca septembra
          </p>
          <p className="text-xs sm:text-sm text-amber-900/80 mt-0.5">
            Akciová ponuka na školský rok pre nových zákazníkov.
          </p>

          <div className={`mt-2 flex items-baseline flex-wrap gap-x-2 ${compact ? '' : 'sm:gap-x-3'}`}>
            <span className="text-xs sm:text-sm text-gray-600 line-through" data-testid={`promo-base-${kind}`}>
              {baseLabel}
            </span>
            <span className={`${compact ? 'text-xl' : 'text-2xl sm:text-3xl'} font-extrabold text-amber-700`} data-testid={`promo-final-${kind}`}>
              {finalLabel}
            </span>
            <span className="text-xs sm:text-sm text-gray-600 font-medium">{periodLabel}</span>
          </div>
        </div>
      </div>
    </div>
  )
}
